import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HairlineCard,
  Stack,
  StatusChip,
  TextField,
  Typography,
  useSnackbar,
} from '@munifocus/ui';
import type { ContactLogEntryView } from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { ContactEntrySummary } from '../components/front-desk/ContactEntrySummary';
import { useCompleteCallback, useFrontDeskStatus, useMyCallbacks } from '../lib/front-desk-api';

function DoneDialog({ entry, onClose }: { entry: ContactLogEntryView; onClose: () => void }) {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const complete = useCompleteCallback();
  const [note, setNote] = useState('');
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.frontDesk.callbacks.doneTitle')}</DialogTitle>
      <DialogContent>
        <TextField
          multiline
          minRows={2}
          fullWidth
          sx={{ mt: 1 }}
          label={t('gov.frontDesk.callbacks.doneNote')}
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 1000))}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('gov.frontDesk.cancel')}</Button>
        <Button
          variant="contained"
          disabled={complete.isPending}
          onClick={() =>
            complete.mutate(
              { id: entry.id, note: note.trim() || undefined },
              {
                onSuccess: () => {
                  snackbar.success(t('gov.frontDesk.callbacks.doneSaved'));
                  onClose();
                },
                onError: () => snackbar.error(t('gov.frontDesk.actionFailed')),
              },
            )
          }
        >
          {t('gov.frontDesk.callbacks.markDone')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Callbacks assigned to the caller: open ones (overdue first) and recently done. */
export function MyCallbacks() {
  const { t } = useTranslation();
  const status = useFrontDeskStatus();
  const mine = useMyCallbacks(Boolean(status.data?.enabled));
  const [completing, setCompleting] = useState<ContactLogEntryView | null>(null);
  const open = [...(mine.data?.open ?? [])].sort((a, b) => {
    const ao = a.callback?.overdue ? 0 : 1;
    const bo = b.callback?.overdue ? 0 : 1;
    if (ao !== bo) return ao - bo;
    return (a.callback?.dueAt ?? '').localeCompare(b.callback?.dueAt ?? '');
  });

  return (
    <AppShell>
      {/* The topbar holds the title (the page's one h1); this says what the
          screen is for. */}
      <PageIntro>{t('gov.frontDesk.callbacks.subtitle')}</PageIntro>

      {(status.isLoading || mine.isLoading) && <CircularProgress size={24} />}
      {(status.isError || mine.isError) && (
        <Alert severity="error">{t('gov.frontDesk.loadFailed')}</Alert>
      )}
      {status.data && !status.data.enabled && (
        <Alert severity="info">{t('gov.frontDesk.disabled')}</Alert>
      )}

      {mine.data && status.data?.enabled && (
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
                {t('gov.frontDesk.callbacks.open', { count: open.length })}
              </Typography>
              {mine.data.overdueCount > 0 && (
                <StatusChip
                  status="emergency"
                  label={t('gov.frontDesk.callbacks.overdueCount', {
                    count: mine.data.overdueCount,
                  })}
                />
              )}
            </Stack>
            <HairlineCard sx={{ p: 0 }}>
              {open.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  {t('gov.frontDesk.callbacks.empty')}
                </Typography>
              ) : (
                open.map((entry) => (
                  <ContactEntrySummary
                    key={entry.id}
                    entry={entry}
                    highlight={entry.callback?.overdue}
                    actions={
                      <Button size="small" variant="contained" onClick={() => setCompleting(entry)}>
                        {t('gov.frontDesk.callbacks.markDone')}
                      </Button>
                    }
                  />
                ))
              )}
            </HairlineCard>
          </Box>

          <Box>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              {t('gov.frontDesk.callbacks.recentlyDone')}
            </Typography>
            <HairlineCard sx={{ p: 0 }}>
              {mine.data.recentlyDone.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  {t('gov.frontDesk.callbacks.noneDone')}
                </Typography>
              ) : (
                mine.data.recentlyDone.map((entry) => (
                  <ContactEntrySummary key={entry.id} entry={entry} />
                ))
              )}
            </HairlineCard>
          </Box>
        </Stack>
      )}

      {completing && <DoneDialog entry={completing} onClose={() => setCompleting(null)} />}
    </AppShell>
  );
}
